import { useEffect } from 'react';
import { useGetIdentity } from '@refinedev/core';
import { useGlobalContext } from '@admin-panel/hooks';
import type { IERPUser } from '@admin-panel/types';
import { isInstalledWindow } from '../hubAlerts/hubWindowMode';
import { getRegistrationStatus, readTestPassedAt } from './hubAlertSetupState';
import { useSharedHubAlertSetup } from './HubAlertSetupGate';
import { browserVersion } from './deviceReport';
import { readInstallId, reportSetupState, setTelemetryUser } from './setupTelemetry';
import { setupStateParams, stateSignature } from './setupTelemetryRules';

/** Settles quick successive checks (open, focus, the setup page's poll) into one report. */
const REPORT_DELAY_MS = 2000;

const readPermission = (): NotificationPermission | 'unsupported' =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

/**
 * Reports this device's order-alert setup state for adoption tracking (see setupTelemetry.ts):
 * once its check settles, whenever it changes, and otherwise every few hours. Renders nothing.
 */
const SetupReporter = () => {
  const setup = useSharedHubAlertSetup();
  const { data: user } = useGetIdentity<IERPUser>();
  const { warehouseId, isAllFacilities } = useGlobalContext();

  useEffect(() => setTelemetryUser(user?.id ?? null), [user?.id]);

  const known = Boolean(user) && !setup.loading && (!setup.applies || setup.settled);
  const hints = (
    navigator as Navigator & {
      userAgentData?: { brands?: Array<{ brand: string; version?: string }> };
    }
  ).userAgentData;
  const params = known
    ? setupStateParams({
        installId: readInstallId(),
        erpUserId: user?.id ?? null,
        facilityIds: user?.adminUserAttributes?.warehouseIds ?? [],
        selectedFacility: isAllFacilities
          ? 'ALL'
          : typeof warehouseId === 'number'
            ? warehouseId
            : null,
        device: setup.device,
        browserVersion: browserVersion(hints?.brands, navigator.userAgent),
        installed: isInstalledWindow(),
        permission: readPermission(),
        registration: getRegistrationStatus(),
        step: setup.step,
        applies: setup.applies,
        mode: setup.mode,
        testPassedAt: readTestPassedAt()?.toISOString() ?? null,
      })
    : null;
  const signature = params ? stateSignature(params) : null;

  useEffect(() => {
    if (!params) return undefined;
    const timer = window.setTimeout(() => reportSetupState(params), REPORT_DELAY_MS);
    return () => window.clearTimeout(timer);
    // Reports follow meaningful changes only (the signature), not every render.
  }, [signature]);

  return null;
};

export default SetupReporter;
